import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  TrendingUp, 
  Target, 
  Video, 
  Brain, 
  BarChart3, 
  Zap, 
  Users, 
  Award,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Settings,
  Eye,
  Megaphone
} from "lucide-react";

const Services = () => {
  const services = [
    {
      icon: <TrendingUp className="h-12 w-12" />,
      title: "Growth Performance Marketing Strategy",
      description: "Comprehensive growth strategies built on data analysis, market research, and performance optimization.",
      features: [
        "Market & competitor analysis",
        "Customer journey mapping",
        "Growth funnel optimization",
        "Performance KPI tracking",
        "ROI-focused campaign planning"
      ],
      badge: "Core Service"
    },
    {
      icon: <Target className="h-12 w-12" />,
      title: "Media Buying & Paid Ads",
      description: "Expert management across all major advertising platforms with focus on ROAS optimization.",
      features: [
        "Meta Ads (Facebook & Instagram)",
        "Google Ads (Search, Display, YouTube)",
        "TikTok Ads & Snapchat Ads",
        "Advanced audience targeting",
        "Bid optimization & budget allocation"
      ],
      badge: "High ROI"
    },
    {
      icon: <Video className="h-12 w-12" />,
      title: "Video Production",
      description: "Professional video content creation that converts viewers into customers across all platforms.",
      features: [
        "Commercial video production",
        "User-generated content (UGC)",
        "Product demonstration videos",
        "Brand storytelling content",
        "Platform-optimized formats"
      ],
      badge: "Creative"
    },
    {
      icon: <Brain className="h-12 w-12" />,
      title: "AI Video Creation & Automation",
      description: "Cutting-edge AI-powered video creation for scalable, cost-effective content production.",
      features: [
        "AI-generated video content",
        "Automated video variations",
        "Personalized video campaigns",
        "Rapid content scaling",
        "Cost-effective production"
      ],
      badge: "AI-Powered"
    },
    {
      icon: <Lightbulb className="h-12 w-12" />,
      title: "Creative Testing & Scaling Framework",
      description: "Systematic approach to creative testing and optimization for maximum performance.",
      features: [
        "A/B testing methodology",
        "Creative performance analysis",
        "Winning creative scaling",
        "Creative fatigue monitoring",
        "Continuous optimization loops"
      ],
      badge: "Data-Driven"
    },
    {
      icon: <BarChart3 className="h-12 w-12" />,
      title: "Funnel Optimization & Conversion Tracking",
      description: "Complete funnel analysis and optimization to maximize conversions at every stage.",
      features: [
        "Conversion funnel analysis",
        "Landing page optimization",
        "Checkout flow improvement",
        "Advanced tracking setup",
        "Attribution modeling"
      ],
      badge: "Conversion Focus"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Audit & Research",
      description: "Deep dive into your current performance, market position, and growth opportunities."
    },
    {
      step: "02", 
      title: "Strategy & Planning",
      description: "Develop comprehensive media strategy and creative framework tailored to your goals."
    },
    {
      step: "03",
      title: "Creative Production",
      description: "Create high-converting content using both human creativity and AI automation."
    },
    {
      step: "04",
      title: "Launch & Optimize",
      description: "Execute campaigns with continuous testing, optimization, and scaling."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6">
              <Zap className="h-3 w-3 mr-1" />
              Complete Growth Solutions
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              Comprehensive performance marketing services designed to scale your brand 
              with data-driven strategies, creative excellence, and AI-powered automation.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-elegant group">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-primary group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {service.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl mb-3">{service.title}</CardTitle>
                  <p className="text-muted-foreground">{service.description}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Our Proven <span className="text-gradient">Process</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A systematic approach to growth that delivers consistent, measurable results.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="card-elegant text-center">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-gradient mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button asChild size="lg" className="btn-glow">
              <Link to="/process">
                Learn More About Our Process
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="card-elegant bg-gradient-to-r from-primary/10 to-primary-glow/10 border-primary/20">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                Ready to <span className="text-gradient">Scale Your Growth</span>?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Let's discuss which services will drive the biggest impact for your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="btn-glow text-lg px-8 py-6">
                  <Link to="/contact">
                    Get Custom Strategy
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-lg px-8 py-6">
                  <Link to="/case-studies">
                    View Success Stories
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Services;